import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  input,
  model,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';
import type { BzInputSize } from '../input/bz-input';

/** The same scale as `bz-input`, aliased so a caller does not import the sibling's type. */
export type BzTextareaSize = BzInputSize;

let nextId = 0;

/**
 * A multi-line text field — `bz-input`'s missing sibling.
 *
 * Deliberately *not* `bz-input` with `type="textarea"`: the affix slots make
 * no sense in a multi-line box, the height is controlled by `rows` rather
 * than by the control-height tokens, and only this one needs to grow as the
 * user types.
 *
 * Defaults come from counting the console's 94 `<textarea>`s:
 *
 * | Attribute | Uses | Here |
 * | --- | ---: | --- |
 * | `ng-model` | 92 | `value`, a `model()` plus `ControlValueAccessor` |
 * | `rows` | 78 (`rows="3"` in 38) | `rows`, defaulting to 3 |
 * | `ng-disabled` | 15 | `disabled` |
 * | `md-maxlength` / `maxlength` | 10 | `maxLength`, which also shows a counter |
 * | `md-no-autogrow` | **0** | so `autoResize` defaults to on |
 *
 * There is no visible label, matching `bz-input` — a label belongs to the
 * `bz-form-field` that does not exist yet. Until it does, name the control
 * with `ariaLabel` or a `<label for>` of the caller's own.
 */
@Component({
  selector: 'bz-textarea',
  templateUrl: './bz-textarea.html',
  styleUrl: './bz-textarea.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BzTextarea),
      multi: true,
    },
  ],
})
export class BzTextarea implements ControlValueAccessor {
  readonly value = model('');
  readonly disabled = model(false);
  readonly placeholder = input('');
  readonly size = input<BzTextareaSize>('md');
  readonly readonly = input(false);
  readonly invalid = input(false);

  /**
   * Starting height in lines. A minimum rather than a fixed height while
   * `autoResize` is on, which is how `md-input-container` behaves — the
   * console sets `rows` on 78 of its 94 textareas and never disables growth.
   */
  readonly rows = input(3);

  /**
   * Caps the value and shows a character counter. Both halves come from the
   * legacy `md-maxlength`, which did exactly this; a bare `maxlength` that
   * silently stops accepting keystrokes is worse than the limit being
   * visible.
   */
  readonly maxLength = input<number | null>(null);

  /**
   * Grows with the content, past `rows` but never below it. On by default
   * because every one of the console's 94 textareas grows today — AngularJS
   * Material autogrows unless `md-no-autogrow` is set, and nothing sets it.
   */
  readonly autoResize = input(true);

  /** Only needed when the field has no visible label next to it. */
  readonly ariaLabel = input('');

  private readonly field = viewChild<ElementRef<HTMLTextAreaElement>>('field');

  protected readonly counterId = `bz-textarea-counter-${nextId++}`;

  protected readonly wrapperClass = computed(() => {
    const classes = ['bz-textarea', `bz-textarea--${this.size()}`];
    if (this.invalid()) classes.push('bz-textarea--invalid');
    if (this.disabled()) classes.push('bz-textarea--disabled');
    if (this.autoResize()) classes.push('bz-textarea--auto');
    return classes.join(' ');
  });

  protected readonly counter = computed(() => {
    const max = this.maxLength();
    return max === null ? null : `${this.value().length}/${max}`;
  });

  constructor() {
    /*
     * Re-measures when the value changes from outside — a form patching it, a
     * parent resetting it — not just on keystrokes. Without this a programmatic
     * multi-line value lands in a box still sized for the old one.
     *
     * An effect rather than a `ngAfterViewInit` + `ngOnChanges` pair because
     * it has to run on the first render too, when the view query has just
     * resolved and `value` already holds whatever the form wrote into it.
     */
    effect(() => {
      this.value();
      this.autoResize();
      this.rows();
      const el = this.field()?.nativeElement;
      if (el) this.resize(el);
    });
  }

  private propagateChange: (value: string) => void = () => undefined;
  private propagateTouched: () => void = () => undefined;

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected onInput(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    this.value.set(el.value);
    this.propagateChange(el.value);
  }

  protected markTouched(): void {
    this.propagateTouched();
  }

  /**
   * Height from `scrollHeight`, which only reports the content's full height
   * once the element is allowed to be shorter than it — so the height is
   * cleared first. Reading it without that returns the height already set and
   * the box never shrinks back after a deletion.
   *
   * The floor is the stylesheet's `min-height`, computed from `rows` and the
   * line height, so this never has to measure a line itself. `rows` alone
   * would not hold it — an inline height beats the attribute.
   */
  private resize(el: HTMLTextAreaElement): void {
    if (!this.autoResize()) {
      el.style.height = '';
      return;
    }

    el.style.height = '';
    el.style.height = `${el.scrollHeight}px`;
  }
}
